import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
// Lib
import { apiClient } from "@/lib/apiClient";
// UI
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
// Icons
import { Heart, ScaleRating, StarFull } from "@/components/icons";
// Components
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";
// Hooks
import { useReportData } from "../../hooks/useReportData";
import { useReportConfig } from "../../hooks/useReportConfig";
// Styles
import styles from "./SurveyCrossTabReportPage.module.scss";

// ---------------------------------------------------------------------------
// API shape (local — backend route: /api/admin/reports/survey-cross-tab)
// ---------------------------------------------------------------------------

export interface SurveyCrossTabItem {
  questionID: string;
  questionText: string;
  surveyID: string;
  surveyName: string;
  responseCount: number;
  avgRating: number;
  csat: number;
  npsScore: number;
  ratingDistribution: Record<string, number>;
}

export interface SurveyCrossTabSummary {
  totalResponses: number;
  totalQuestions: number;
  avgRating: number;
  avgNps: number;
  avgCsat: number;
  responseRatePercent: number;
}

export interface SurveyCrossTabMonthlyPoint {
  month: string;
  csat: number;
  nps: number;
}

export interface SurveyCrossTabBreakdown {
  label: string;
  npsScore: number;
  responseCount: number;
}

export interface SurveyCrossTabPivotCell {
  row: string;
  col: string;
  value: number;
}

export interface SurveyCrossTabResponse {
  summary: SurveyCrossTabSummary;
  monthlyTrend: SurveyCrossTabMonthlyPoint[];
  byEducation: SurveyCrossTabBreakdown[];
  byDepartment: SurveyCrossTabBreakdown[];
  pivotMatrix: SurveyCrossTabPivotCell[];
  items: SurveyCrossTabItem[];
  count: number;
}

export interface SurveyCrossTabParams {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  surveyID?: string;
  educationID?: string;
  departmentID?: string;
}

const PAGE_SIZE = 20;
const ENDPOINT = "/api/admin/reports/survey-cross-tab";

/** TransformResponseInterceptor `{ data: <payload> }` döner — güvenli unwrap. */
function unwrap<T>(body: { data?: T } | T): T {
  if (body && typeof body === "object" && "data" in (body as object)) {
    const d = (body as { data?: T }).data;
    if (d !== undefined) return d;
  }
  return body as T;
}

async function fetchSurveyCrossTab(
  params: SurveyCrossTabParams,
  signal: AbortSignal,
): Promise<SurveyCrossTabResponse> {
  const res = await apiClient.get<SurveyCrossTabResponse>(ENDPOINT, {
    params,
    signal,
  });
  return unwrap<SurveyCrossTabResponse>(
    res.data as unknown as { data?: SurveyCrossTabResponse } | SurveyCrossTabResponse,
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const npsBadgeClass = (score: number) => {
  if (score >= 30) return styles.npsGood;
  if (score < 0) return styles.npsBad;
  return styles.npsWarn;
};

const formatNumber = (n: number | null | undefined) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("tr-TR").format(n);
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SurveyCrossTabReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: SurveyCrossTabParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      departmentID: filter.departmentID,
      search: filter.search?.trim() || undefined,
    }),
    [filter, page],
  );

  const fetcher = useCallback(
    (p: SurveyCrossTabParams, signal: AbortSignal) => fetchSurveyCrossTab(p, signal),
    [],
  );

  const { data, isLoading, error } = useReportData<
    SurveyCrossTabResponse,
    SurveyCrossTabParams
  >({
    fetcher,
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const monthly = data?.monthlyTrend ?? [];
  const byDepartment = data?.byDepartment ?? [];
  const pivot = data?.pivotMatrix ?? [];

  // ---- Trend chart series (NPS + CSAT) ----
  const trendCategories = useMemo(() => monthly.map((m) => m.month), [monthly]);
  const trendSeries = useMemo(
    () => [
      {
        name: t("reports.surveyCrossTab.seriesNps"),
        data: monthly.map((m) => m.nps),
      },
      {
        name: t("reports.surveyCrossTab.seriesCsat"),
        data: monthly.map((m) => m.csat),
      },
    ],
    [monthly, t],
  );

  // ---- Department NPS bar ----
  const deptCategories = useMemo(() => byDepartment.map((b) => b.label), [byDepartment]);
  const deptSeries = useMemo(
    () => [
      {
        name: t("reports.surveyCrossTab.seriesNps"),
        data: byDepartment.map((b) => b.npsScore),
      },
    ],
    [byDepartment, t],
  );

  // ---- Heatmap: question × department ----
  const heatmapSeries = useMemo(() => {
    if (pivot.length === 0) return [];
    const rows = Array.from(new Set(pivot.map((p) => p.row)));
    const cols = Array.from(new Set(pivot.map((p) => p.col)));
    return rows.map((row) => ({
      name: row,
      data: cols.map((col) => {
        const cell = pivot.find((p) => p.row === row && p.col === col);
        return { x: col, y: cell?.value ?? 0 };
      }),
    }));
  }, [pivot]);

  // ---- NPS Gauge (radialBar) — avgNps -100..100 → 0..100 -----
  const gaugeSeries = useMemo(() => {
    const v = summary?.avgNps ?? 0;
    return [Math.max(0, Math.min(100, Math.round((v + 100) / 2)))];
  }, [summary]);

  // ---- Table columns ----
  const columns: TableColumn<SurveyCrossTabItem>[] = useMemo(
    () => [
      {
        header: t("reports.surveyCrossTab.headerQuestion"),
        accessor: (row) => (
          <span className={styles.questionCell} title={row.questionText}>
            {row.questionText || "—"}
          </span>
        ),
      },
      {
        header: t("reports.surveyCrossTab.headerSurvey"),
        accessor: (row) => row.surveyName || "—",
      },
      {
        header: t("reports.surveyCrossTab.headerResponses"),
        accessor: (row) => formatNumber(row.responseCount),
      },
      {
        header: t("reports.surveyCrossTab.headerAvgRating"),
        accessor: (row) => row.avgRating.toFixed(2),
      },
      {
        header: t("reports.surveyCrossTab.headerCsat"),
        accessor: (row) => row.csat.toFixed(2),
      },
      {
        header: t("reports.surveyCrossTab.headerNps"),
        accessor: (row) => (
          <span className={`${styles.npsBadge} ${npsBadgeClass(row.npsScore)}`}>
            {row.npsScore.toFixed(0)}
          </span>
        ),
      },
    ],
    [t],
  );

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<SurveyCrossTabItem>({
    reportKey: "SURVEY_CROSS_TAB",
    reportTitle: t("reports.surveyCrossTab.title"),
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("reports.surveyCrossTab.title")}
      layoutKey={`report:SURVEY_CROSS_TAB`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reportCatalog.categories.quality.title")}
      description={t("reports.surveyCrossTab.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="SURVEY_CROSS_TAB"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="anket-cross-tab"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
        />
      }
    >
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("reports.surveyCrossTab.totalResponses")}
          value={formatNumber(summary?.totalResponses ?? 0)}
          icon={<Heart width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.surveyCrossTab.totalQuestions")}
          value={formatNumber(summary?.totalQuestions ?? 0)}
          icon={<ScaleRating width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.surveyCrossTab.avgNps")}
          value={summary ? summary.avgNps.toFixed(0) : "—"}
          icon={<StarFull width={18} height={18} />}
          accent={
            summary && summary.avgNps >= 30
              ? "success"
              : summary && summary.avgNps < 0
                ? "danger"
                : "warning"
          }
        />
        <ReportKPICard
          title={t("reports.surveyCrossTab.avgCsat")}
          value={summary ? summary.avgCsat.toFixed(2) : "—"}
          icon={<Heart width={18} height={18} />}
          accent="success"
        />
      </div>

      <div className={styles.chartGrid}>
        <ReportChart
          title={t("reports.surveyCrossTab.trendTitle")}
          type="line"
          isLoading={isLoading}
          isEmpty={monthly.length === 0}
          series={trendSeries}
          options={{
            xaxis: { categories: trendCategories },
            stroke: { curve: "smooth", width: 2 },
            yaxis: [
              { title: { text: t("reports.surveyCrossTab.seriesNps") } },
              { opposite: true, title: { text: t("reports.surveyCrossTab.seriesCsat") } },
            ],
            colors: ["#4f46e5", "#10b981"],
          }}
        />

        <ReportChart
          title={t("reports.surveyCrossTab.deptNpsTitle")}
          type="bar"
          isLoading={isLoading}
          isEmpty={byDepartment.length === 0}
          series={deptSeries}
          options={{
            xaxis: { categories: deptCategories },
            plotOptions: {
              bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
            },
            colors: ["#0ea5e9"],
          }}
        />
      </div>

      <div className={styles.gaugeWrap}>
        <ReportChart
          title={t("reports.surveyCrossTab.gaugeTitle")}
          type="radialBar"
          height={280}
          isLoading={isLoading}
          isEmpty={!summary}
          series={gaugeSeries}
          options={{
            plotOptions: {
              radialBar: {
                startAngle: -135,
                endAngle: 135,
                hollow: { size: "55%" },
                track: { background: "#e2e8f0", strokeWidth: "100%" },
                dataLabels: {
                  name: { show: true, fontSize: "14px", color: "#64748b" },
                  value: {
                    show: true,
                    fontSize: "26px",
                    fontWeight: 600,
                    formatter: () =>
                      `${(summary?.avgNps ?? 0).toFixed(0)}`,
                  },
                },
              },
            },
            labels: [t("reports.surveyCrossTab.avgNps")],
            colors: ["#4f46e5"],
          }}
        />
      </div>

      <div className={styles.heatmapWrap}>
        <ReportChart
          title={t("reports.surveyCrossTab.heatmapTitle")}
          type="heatmap"
          height={Math.max(280, heatmapSeries.length * 40 + 80)}
          isLoading={isLoading}
          isEmpty={heatmapSeries.length === 0}
          series={heatmapSeries}
          options={{
            dataLabels: { enabled: true, style: { fontSize: "11px" } },
            plotOptions: {
              heatmap: {
                shadeIntensity: 0.5,
                radius: 4,
                colorScale: {
                  ranges: [
                    { from: 0, to: 0, color: "#f1f5f9", name: "—" },
                    { from: 0.01, to: 4, color: "#fee2e2", name: "0-4" },
                    { from: 4.01, to: 7, color: "#fef3c7", name: "4-7" },
                    { from: 7.01, to: 10, color: "#dcfce7", name: "7-10" },
                  ],
                },
              },
            },
          }}
        />
      </div>

      {cfg.columnSettings}

      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.questionID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.surveyCrossTab.noData")
            : t("reports.surveyCrossTab.noResponse")
        }
      />
    </ReportDetailLayout>
  );
};

export default SurveyCrossTabReportPage;
